export const frontdeskTicketTypes = new Set(['callback', 'email_back', 'human_escalation', 'other']);

// Website-chat escalations are stored with session_id 'chat:<conversation_id>'.
export const callbackChatId = (ticket) => {
  const session = String(ticket?.session_id || '');
  if (session.startsWith('chat:') && session.length > 5) return session.slice(5);
  return ticket?.metadata?.source === 'website_chat' && ticket?.metadata?.conversation_id
    ? String(ticket.metadata.conversation_id) : null;
};

export const callbackCallId = (ticket) => {
  if (callbackChatId(ticket)) return null;
  const id = ticket?.call_session_id || ticket?.session_id;
  return id && !['manual', 'web'].includes(id) ? id : null;
};

// Where the request came from: 'web' (website chat), 'voice' (a call) or 'manual'.
export const callbackSource = (ticket) => {
  if (callbackChatId(ticket) || ticket?.ticket_source === 'web') return 'web';
  if (callbackCallId(ticket) || ticket?.ticket_source === 'voice') return 'voice';
  return 'manual';
};

export const OVERDUE_BUSINESS_DAYS = 2;

// Deadline = created time moved forward N weekdays (Mon–Fri), in the browser's time zone.
export const addBusinessDays = (start, days) => {
  const date = new Date(start);
  let added = 0;
  while (added < days) {
    date.setDate(date.getDate() + 1);
    const day = date.getDay();
    if (day !== 0 && day !== 6) added += 1;
  }
  return date;
};

export const isCallbackOverdue = (ticket, now = new Date()) => {
  if (ticket?.status !== 'pending' || !ticket?.created_at) return false;
  // G2: when the request stored the promise made to the caller, that is the deadline.
  const due = ticket?.sla_due_at || ticket?.metadata?.sla?.due_at;
  if (due) {
    const dueAt = new Date(due);
    if (!Number.isNaN(dueAt.getTime())) return dueAt.getTime() < new Date(now).getTime();
  }
  const created = new Date(ticket.created_at);
  if (Number.isNaN(created.getTime())) return false;
  return addBusinessDays(created, OVERDUE_BUSINESS_DAYS).getTime() < new Date(now).getTime();
};

// The nav badge listens for this to refresh right after the list changes.
export const CALLBACKS_CHANGED_EVENT = 'aiker:callback-requests-changed';
export const announceCallbacksChanged = () => {
  try { window.dispatchEvent(new Event(CALLBACKS_CHANGED_EVENT)); } catch { /* non-browser */ }
};

export const followupCopy = (lang) => lang === 'zh' ? {
  title: '回电与待跟进', summary: '查看来电后需要人工处理的事项，记录联系结果与下一步安排。',
  create: '新建跟进', target: '需要联系的人或部门', assignee: '处理人',
  result: '本次联系结果 / 跟进备注', next: '下一步安排', history: '跟进记录',
  save: '保存跟进', saved: '跟进已保存', error: '保存失败，请重试',
  originalCall: '查看原通话', related: '查看关联跟进',
  empty: '暂无跟进记录', unassigned: '未指定', status: '处理状态',
  detail: '查看与跟进', search: '搜索姓名、电话、事由或处理人',
  resultRequired: '完成前请记录处理结果',
  overdue: '已超时', overdueHint: '超过 2 个工作日仍未处理',
  sourceVoice: '电话', sourceWeb: '网页客服', sourceManual: '手动创建',
  originalChat: '查看原对话', pickAssignee: '从团队或通讯录选择…',
  teamMembers: '团队成员', directory: '通讯录',
  pendingBadge: (count) => `${count} 条待处理回电`,
} : {
  title: 'Callbacks & follow-up', summary: 'Handle requests from calls and record contact outcomes and next steps.',
  create: 'New follow-up', target: 'Person or department to contact', assignee: 'Handled by',
  result: 'Contact outcome / follow-up note', next: 'Next step', history: 'Follow-up history',
  save: 'Save follow-up', saved: 'Follow-up saved', error: 'Could not save. Please retry.',
  originalCall: 'View original call', related: 'View related follow-up',
  empty: 'No follow-up recorded yet', unassigned: 'Not assigned', status: 'Status',
  detail: 'View / follow up', search: 'Search name, phone, reason or handler',
  resultRequired: 'Record the outcome before completing this request',
  overdue: 'Overdue', overdueHint: 'Still pending after 2 business days',
  sourceVoice: 'Phone', sourceWeb: 'Web chat', sourceManual: 'Manual',
  originalChat: 'View original chat', pickAssignee: 'Pick from team or directory…',
  teamMembers: 'Team members', directory: 'Directory',
  pendingBadge: (count) => `${count} pending callback${count === 1 ? '' : 's'}`,
};
